<template>
    <div :class="`${tablesCached ? 'd-flex' : 'd-none'} flex-column w-100`">
      <div v-if="!isSimulation" class="mb-3 flex-shrink-0">
        <bulk-reassign :inspection-ids-to-assign="selectedInspectionIds"/>
      </div>
      <div class="flex-grow-1 w-100">
        <el-table
          ref="routingTable"
          class="wrap-whole-words w-100 flex-grow-1"
          :default-sort="tableFormat === constants.ROUTING_TABLE_FORMATS.BY_INSPECTOR.value
           ? {prop: 'inspectorName', order: 'ascending'}
            : {prop: 'inspections.length', order: 'descending'}"
          @row-click="handleRowClick"
          @expand-change="handleExpandChange"
          @select="handleSelect"
          @select-all="handleSelectAll"
          :select-on-indeterminate="true"
          :highlight-current-row="true"
          :current-row-key="currentRowKey"
          :cell-class-name="cellClassName"
          :header-cell-class-name="headerCellClassName"
          :row-class-name="rowClassName"
          :span-method="parentRowSpanAllColumns"
          :data="tableData"
          :max-height="420"
          border
          row-key="rowId"
          :tree-props="{children: 'inspections', hasChildren: 'hasChildren'}">
          <el-table-column
            v-if="!isSimulation"
            type="selection"
            align="center"
            :reserve-selection="false"
            fixed="left"
            width="40">
          </el-table-column>
          <!-- Group By Inspector - Merged parent row / tooltip on child row-->
          <el-table-column
            v-if="tableFormat === constants.ROUTING_TABLE_FORMATS.BY_INSPECTOR.value"
            label=""
            prop="inspectorName"
            width="35px">
            <template #header>
              <routing-tooltip />
            </template>
            <template v-slot="props">
              <span v-if="props.row.inspections">
                {{ props.row.inspectorName }} &nbsp;&nbsp;<strong>({{ props.row.inspections.length }})</strong>
              </span>
              <div v-else class="d-flex align-items-center">
                <routing-tooltip :row="props.row" />
              </div>
            </template>
          </el-table-column>
          <!-- Group By Project - Merged parent row / tooltip on child row-->
          <el-table-column
            v-if="tableFormat === constants.ROUTING_TABLE_FORMATS.BY_PROJECT.value"
            label=""
            prop="inspections.length"
            width="35px">
            <template #header>
              <routing-tooltip />
            </template>
            <template v-slot="props">
              <span v-if="props.row.inspections">
                {{ props.row.projectName }} &nbsp;&nbsp;<strong>({{ props.row.inspections.length }})</strong>&nbsp;
                <router-link :to="`/projects/${props.row.inspections[0].project.id}`">(link)</router-link>
              </span>
              <div v-else class="d-flex align-items-center">
                <routing-tooltip :row="props.row" />
              </div>
            </template>
          </el-table-column>
    <!--        Inspection Type - same position in both tables -->
          <el-table-column
            label="Inspection Type"
            prop="inspectionType"
            :min-width="190">
            <template v-slot="props">
              <div v-if="!props.row.inspections">
                {{ props.row.inspectionType }}
              </div>
            </template>
          </el-table-column>
    <!--        Group By Inspector - Project Name column-->
          <el-table-column
            v-if="tableFormat === constants.ROUTING_TABLE_FORMATS.BY_INSPECTOR.value"
            label="Project"
            prop="projectName"
            :min-width="160">
            <template v-slot="props">
              <div v-if="!props.row.inspections">
                <router-link :to="`/projects/${props.row.project.id}`">{{ props.row.projectName }}</router-link>
              </div>
            </template>
          </el-table-column>
    <!--        Group by Project - Assigned inspector column-->
          <el-table-column
            v-if="tableFormat === constants.ROUTING_TABLE_FORMATS.BY_PROJECT.value"
            label="Assigned Inspector"
            prop="assignedInspector"
            :min-width="160">
            <template v-slot="props">
              <div v-if="!props.row.inspections">
                {{ props.row.assignedInspector.name }}
              </div>
            </template>
          </el-table-column>
          <el-table-column
            v-if="isSimulation"
            label="Actual"
            prop="actualInspector"
            :min-width="120">
            <template v-slot="props">
              <div v-if="!props.row.inspections && props.row.actualInspector">
                {{ props.row.actualInspector.name }}
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="pagination-info mb-auto flex-shrink-0">
        <span class="category">{{ tableCountMessage }}</span>
      </div>
      <div v-if="!isSimulation" class="d-flex justify-content-end flex-shrink-0">
        <save-assignments-button :assignment-ids-to-save="selectedInspectionIds" />
      </div>
  </div>
</template>

<script>
import Constants from '@/utilities/constants'
import {getTableHeight} from '@/utilities/utils'
import {sync} from 'vuex-pathify'
import RoutingTooltip from './RoutingTooltip'
import InspectorSelect from '@/components/Dashboard/Views/InspectorRouting/InspectorSelect.vue'
import BulkReassign from '@/components/Dashboard/Views/InspectorRouting/BulkReassign.vue'
import SaveAssignmentsButton from '@/components/Dashboard/Views/InspectorRouting/SaveAssignmentsButton.vue'

export default {
  components: {SaveAssignmentsButton, BulkReassign, InspectorSelect, RoutingTooltip },
  props: ['tableFormat', 'tablesCached'],
  computed: {
    isSimulation: sync('inspectorRouting/solverParams@isSimulation'),
    selectedInspectionIds: sync('inspectorRouting/selectedInspectionIds'),
    // Hacky fix for reactivity of selectedInspectionIds :Set (Vue2) with changeTracker
    selectedInspectionIdsReactive() {
      return this.selectedInspectionIdsChangeTracker && this.selectedInspectionIds
    },
    tableData() {
      if (this.tableFormat === Constants.ROUTING_TABLE_FORMATS.BY_INSPECTOR.value) {
        return this.$store.get('inspectorRouting/getInspectionsByInspector')
      } else if (this.tableFormat === Constants.ROUTING_TABLE_FORMATS.BY_PROJECT.value) {
        return this.$store.get('inspectorRouting/getInspectionsByProject')
      }
    },
    inspectionCount() {
      return this.$store.get('inspectorRouting/results').length
    },
    tableCountMessage() {
      let parentRowType
      if (this.tableFormat === Constants.ROUTING_TABLE_FORMATS.BY_INSPECTOR.value) {
        parentRowType = 'inspectors'
      } else if (this.tableFormat === Constants.ROUTING_TABLE_FORMATS.BY_PROJECT.value) {
        parentRowType = 'projects'
      }

      return `${ this.selectedInspectionIdsReactive.size} / ${ this.inspectionCount } inspections selected.  \
      (${ this.tableData.length } ${ parentRowType })`
    },
  },
  data() {
    return {
      constants: Constants,
      currentRow: null,
      currentRowKey: null,
      selectedInspectionIdsChangeTracker: 1,
      toggling: false
    }
  },
  methods: {
    tableHeight(buffer) {
      return getTableHeight(buffer)
    },
    cellClassName({row, column, rowIndex, columnIndex}) {
      // no selection column in simulation mode - shift cellClass logic
      if (this.isSimulation) {
        columnIndex +=1
      }

      // checkbox styling for none/all/indeterminate
      // Parent Row - Checkbox
      if (row.inspections && columnIndex === 0) {
        let selectedChildCount = 0
        for (const childRow of row.inspections) {
          if (this.selectedInspectionIds.has(childRow.ptracTaskId)) {
            selectedChildCount++
          }
        }
        // I have side-effects in here . . . since cell-class loops through on render,
        // im taking advantage and toggling selection here too
        if (selectedChildCount === 0) {
          this.$refs.routingTable.toggleRowSelection(row, false)
          return 'custom-checkbox'
        } else if (selectedChildCount === row.inspections.length) {
          this.$refs.routingTable.toggleRowSelection(row, true)
          return 'custom-checkbox'
        } else {
          this.$refs.routingTable.toggleRowSelection(row, false)
          return 'custom-checkbox indeterminate'
        }
      }

      // Child Row Checkbox  -needed to prevent indentation of child row (info column does that)
      if (columnIndex === 0 && !this.isSimulation) {
        return 'custom-checkbox'
      }

      // indent parent row / Background color for tooltip column
      if (columnIndex === 1) {
        if (row.inspections) {
          return 'parent-row'
        }
        if (row.hasOnHoldClient) {
          return 'tooltip-cell has-on-hold-client'
        }
        if (row.manuallyReassigned) {
          return 'tooltip-cell manually-reassigned'
        }
        if (row.unavailableManuallyAssignedInspector) {
          return 'tooltip-cell unavailable-manually-assigned-inspector'
        }
        if (row.manuallyAssignedInspector) {
          return 'tooltip-cell manually-assigned-inspector'
        }
        if (row.reinspection) {
          return 'tooltip-cell reinspection'
        }
        return 'tooltip-cell'
      }

      if (column.property === "actualInspector" && row.actualInspector) {
        if (row.actualInspector.id === row.assignedInspector.id) {
          return 'assignment-match'
        } else {
          return 'assignment-non-match'
        }
      }
    },
    headerCellClassName({columnIndex}) {
      if (this.isSimulation && columnIndex === 1 || !this.isSimulation && columnIndex === 0) {
        return 'header-tooltip'
      }
    },
    rowClassName({row, rowIndex}) {
      //TODO: make expanded rows shaded, to make it easier on the eyes
      return ""
    },
    parentRowSpanAllColumns({ row, column, rowIndex, columnIndex }) {
      // no selection column in simulation mode - shift rowspan logic
      if (this.isSimulation) {
        columnIndex += 1
      }

      if (row.inspections) {
        if (columnIndex === 1) {
          return {
            rowspan: 1,
            colspan: 5 // set as max number, ok if more
          }
        }
      } else {
        return {
          rowspan: 1,
          colspan: 1
        }
      }
    },
    handleRowClick(row){
      if (row.inspections) {
        this.$refs.routingTable.toggleRowExpansion(row)
      } else {
        if (!this.isSimulation) {
          this.$eventHub.$emit('show-modal-1', "Change Assignment", row, '800px')
        }
      }
    },
    handleExpandChange(row, expanded) {
      if (expanded) {
        if (this.currentRow) {
          this.stopBounce(this.currentRow)
        }
        this.currentRow = row
        this.currentRowKey = row.rowId
        this.startBounce(row)
      } else {
        this.currentRowKey = null
        this.stopBounce(row)
      }
    },
    handleSelect(selection, row) {
      if (selection.includes(row)) {
        // user toggled the row to selected
        if (row.inspections) {
          // parent row selected - select all children and add them to selectedInspectionIds
          for (const nestedRow of row.inspections) {
            this.selectedInspectionIds.add(nestedRow.ptracTaskId)
            this.selectedInspectionIdsChangeTracker++
            this.$refs.routingTable.toggleRowSelection(nestedRow, true)
          }
        } else {
          // child row selected - add to selected
          this.selectedInspectionIds.add(row.ptracTaskId)
          this.selectedInspectionIdsChangeTracker++
        }

      } else {
        // user toggled the row to deselected
        if (row.inspections) {
          // parent row selected - deselect all the children and remove from selectedInspectionIds
          for (const nestedRow of row.inspections) {
            this.selectedInspectionIds.delete(nestedRow.ptracTaskId)
            this.selectedInspectionIdsChangeTracker++
            this.$refs.routingTable.toggleRowSelection(nestedRow, false)
          }
        } else {
          this.selectedInspectionIds.delete(row.ptracTaskId)
          this.selectedInspectionIdsChangeTracker++
        }
      }
    },
    handleSelectAll(selection) {
      const selectedParentRows = selection.filter(row => row.inspections)

      if (selectedParentRows.length > 0) {
        for (const parentRow of selectedParentRows) {
          for (const childRow of parentRow.inspections) {
            this.selectedInspectionIds.add(childRow.ptracTaskId)
            this.selectedInspectionIdsChangeTracker++
            this.$refs.routingTable.toggleRowSelection(childRow, true)
          }
        }
      } else {
        this.selectedInspectionIds = new Set()
        for (const parentRow of this.$refs.routingTable.data) {
          for (const childRow of parentRow.inspections) {
            this.$refs.routingTable.toggleRowSelection(childRow, false)
          }
        }
      }
    },
    startBounce(inspector) {
      this.$eventHub.$emit('startBounce', inspector)
    },
    stopBounce(inspector) {
      this.$eventHub.$emit('stopBounce', inspector)
    },
    toggleSelectedRowsFromSelectedInspections() {
      // TODO: this problem doesnt seem to be happening anymore - leave check for now. Only happens when error in table rendering
      if (!this.$refs.routingTable) {
        console.log("couldnt update selection, table not loaded yet")
        setTimeout(this.toggleSelectedRowsFromSelectedInspections, 50)
        return
      }
      for (const parentRow of this.$refs.routingTable.data) {
        for (const childRow of parentRow.inspections) {
          if (this.selectedInspectionIds.has(childRow.ptracTaskId)) {
            this.$refs.routingTable.toggleRowSelection(childRow, true)
          } else {
            this.$refs.routingTable.toggleRowSelection(childRow, false)
          }
        }
      }
    }
  },
  async mounted() {
    this.$eventHub.$on('assignmentChanged', (changedRow) => {
      // Only handle event if emitted with a 'row' payload (from ChangeAssignmentModal) not BulkReassign (they're already selected)
      if (changedRow) {
        this.selectedInspectionIdsChangeTracker++
        this.$refs.routingTable.toggleRowSelection(changedRow, true)
      }
    })
  },
  activated() {
    this.selectedInspectionIdsChangeTracker++
    setTimeout(this.toggleSelectedRowsFromSelectedInspections, 100)
  },
  beforeDestroy() {
    this.$eventHub.$off('assignmentChanged')
  },
  watch: {
    tableData: {
      handler: function() {
        this.$refs.routingTable.doLayout()
        setTimeout(this.toggleSelectedRowsFromSelectedInspections, 100)
      }
    }
  }
}
</script>

<style scoped>
>>>.manually-assigned-inspector {
  background-image:
    radial-gradient(
      circle closest-side,
      white 50%,
      #ffd99b
    )!important;;
  background-color: #ffd99b!important;
}

>>>.unavailable-manually-assigned-inspector {
  background-image:
    radial-gradient(
      circle closest-side,
      white 50%,
      #ff6666
    )!important;
  background-color: #ff6666!important;
}

>>>.manually-reassigned {
  background-image:
    radial-gradient(
      circle closest-side,
      white 50%,
      #87CB16
    )!important;;
  background-color: #87CB16!important;
}

>>>.reinspection {
  background-image:
    radial-gradient(
      circle closest-side,
      white 50%,
      #5cd8ff
    )!important;
  background-color: #5cd8ff!important;
}

>>>.has-on-hold-client {
  background-image:
    radial-gradient(
      circle closest-side,
      white 50%,
      #cc2222
    )!important;
  background-color: #cc2222!important;
}
</style>
